// @ts-nocheck
// Seletor de forma de pagamento da etapa "Confirmar" da página pública — lista compacta (versão B).
// Quais meios existem (pixEnabled / cardEnabled / cashEnabled), a opção inicial, os valores, o sinal e o
// desconto do Pix continuam decididos pela BookingPage; aqui só se desenha a escolha.
import React from 'react';
import { Box, Typography } from '@mui/material';
import { CreditCard, Storefront, Check } from '@mui/icons-material';

export type PayMethod = 'pix' | 'card' | 'cash';

// Símbolo oficial do Pix. Os 3 caminhos abaixo vêm do arquivo ORIGINAL do Banco Central
// (kit "Marca" da Área dos participantes: simbolo_pix_policromia_positivo.pdf), convertidos
// mecanicamente para SVG — só translação e inversão do eixo Y, nenhum ponto alterado.
// O viewBox recorta a prancheta (que tem margem) rente ao símbolo, para o ícone medir
// exatamente o tamanho pedido (manual do BCB v1.6: mínimo de 24 px; sem filtro, sombra,
// gradiente ou contorno; verde oficial #77B6A8, preto ou branco).
const PIX_PATH =
  'M70.054 160.908C76.596 160.908 82.749 158.361 87.375 153.737L112.384 128.727C114.14 126.966 117.201 126.971 118.956 128.727L144.057 153.828C148.684 158.452 154.836 160.999 161.379 160.999L166.307 160.999L134.632 192.674C124.739 202.567 108.7 202.567 98.807 192.674L67.041 160.908L70.054 160.908Z ' +
  'M161.379 72.444C154.836 72.444 148.684 74.991 144.057 79.615L118.956 104.721C117.149 106.529 114.197 106.536 112.384 104.719L87.375 79.706C82.749 75.083 76.596 72.535 70.054 72.535L67.042 72.535L98.807 40.77C108.7 30.877 124.739 30.877 134.632 40.77L166.306 72.444L161.379 72.444Z ' +
  'M40.767 98.809L59.688 79.888L70.054 79.888C74.566 79.888 78.983 81.718 82.171 84.909L107.181 109.919C109.522 112.259 112.597 113.43 115.669 113.43C118.744 113.43 121.817 112.259 124.157 109.922L149.26 84.818C152.449 81.627 156.866 79.797 161.379 79.797L173.659 79.797L192.672 98.809C202.565 108.702 202.565 124.741 192.672 134.634L173.66 153.645L161.379 153.646C156.866 153.646 152.449 151.816 149.26 148.625L124.159 123.524C119.622 118.992 111.714 118.989 107.181 123.526L82.171 148.534C78.983 151.724 74.566 153.555 70.054 153.555L59.687 153.554L40.767 134.634C30.874 124.741 30.874 108.702 40.767 98.809Z';

export const PixSymbol = ({ size = 26, color = '#77B6A8' }: { size?: number; color?: string }) => (
  <svg viewBox="30.874 30.877 171.691 171.69" width={size} height={size} aria-hidden="true" focusable="false" style={{ display: 'block', flexShrink: 0 }}>
    <path fill={color} d={PIX_PATH} />
  </svg>
);

const money = (v: number) => `R$ ${Number(v || 0).toFixed(2).replace('.', ',')}`;

// Cor legível do "check" sobre a cor de destaque da empresa (configurável).
const onAccent = (hex: string) => {
  try {
    const h = (hex || '').replace('#', '');
    if (h.length < 6) return '#fff';
    const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.62 ? '#111827' : '#ffffff';
  } catch { return '#fff'; }
};

// Cor de destaque da empresa (configurável). Quando ela não tem contraste de componente (>= 3:1) com o fundo da opção,
// o anel e o controle de seleção usam o MESMO matiz, clareado (fundo escuro) ou escurecido (fundo claro) só até passar.
// Cores que já passam não mudam.
const toRgb = (hex: string) => { const h = (hex || '').replace('#', ''); return h.length >= 6 ? [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)) : null; };
const lumOf = (c: number[]) => { const f = (v: number) => { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); }; return 0.2126 * f(c[0]) + 0.7152 * f(c[1]) + 0.0722 * f(c[2]); };
const ratioOf = (a: number[], b: number[]) => { const x = lumOf(a), y = lumOf(b); return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05); };
const accentFor = (hex: string, surfaceHex: string) => {
  const c = toRgb(hex), bg = toRgb(surfaceHex);
  if (!c || !bg) return hex;
  const tint = bg.map((v, i) => Math.round(c[i] * 0.0706 + v * 0.9294)); // fundo da opção selecionada (destaque a 7% sobre a superfície)
  const alvo = lumOf(bg) < 0.5 ? 255 : 0;
  let t = 0, out = c;
  while (Math.min(ratioOf(out, bg), ratioOf(out, tint)) < 3.1 && t < 0.8) { t += 0.04; out = c.map((v) => Math.round(v + (alvo - v) * t)); }
  return '#' + out.map((v) => v.toString(16).padStart(2, '0')).join('');
};

interface Props {
  value: PayMethod;
  onChange: (m: PayMethod) => void;
  pixEnabled: boolean;
  cardEnabled: boolean;
  cashEnabled: boolean;
  pixDiscount: number;
  accentColor: string;
  T: any;
  isPartial: boolean;
  total: number;
  payNow: number;
  disabled?: boolean;
}

export default function PaymentMethodSelector(p: Props) {
  const { value, onChange, pixEnabled, cardEnabled, cashEnabled, pixDiscount, accentColor, T, isPartial, total, payNow, disabled } = p;
  const textP = T?.textPrimary ?? '#1a1a1a';
  const textS = T?.textSecondary ?? '#566070'; // descrição: >= 4.5:1 também sobre o fundo selecionado
  const neutralBorder = T?.border ?? '#e5e7eb';
  const controlBorder = T ? '#7b8494' : '#6f7b8b'; // anel do controle com contraste de componente (>= 3:1)
  const surface = T?.surfaceAlt ?? '#fafafa';
  const ring = textP; // foco: anel neutro de alto contraste (não depende da cor da empresa e não se confunde com "selecionado")
  const accentUI = accentFor(accentColor, surface); // anel e controle de seleção
  const accentInk = onAccent(accentUI);

  const options = [
    pixEnabled && {
      id: 'pix' as PayMethod, title: 'Pix', icon: <PixSymbol size={26} />,
      desc: pixDiscount > 0 ? `${pixDiscount}% de desconto · QR Code ou copia e cola` : 'QR Code ou Pix copia e cola',
    },
    cardEnabled && {
      // Sem texto de parcelas nem de crédito/débito aqui: quem escolhe (crédito ou débito, parcelas) é a própria pessoa, no formulário do
      // Mercado Pago, na etapa seguinte.
      id: 'card' as PayMethod, title: 'Cartão de crédito', icon: <CreditCard sx={{ fontSize: 26, color: textP }} />,
      desc: 'Pague online com seu cartão',
    },
    cashEnabled && {
      id: 'cash' as PayMethod, title: 'Pagar no local', icon: <Storefront sx={{ fontSize: 26, color: textP }} />,
      desc: 'Pague no atendimento',
    },
  ].filter(Boolean) as any[];

  // Selo "Recomendado" no Pix (como no visual anterior). Com uma única opção não há o que recomendar.
  const showBadge = (o: any) => o.id === 'pix' && options.length > 1;

  // Linha de resumo: deixa explícita a diferença entre sinal, pagamento integral e pagar no atendimento.
  const summary = value === 'cash'
    ? `Nada a pagar agora. ${money(total)} no atendimento.`
    : isPartial
      ? `Sinal de ${money(payNow)} agora. Restante de ${money(Math.max(0, total - payNow))} no atendimento.`
      : `Pagamento integral de ${money(payNow)} agora.`;

  const Control = ({ checked }: { checked: boolean }) => (
    <Box aria-hidden="true" sx={{
      width: 22, height: 22, borderRadius: '50%', flexShrink: 0, display: 'grid', placeItems: 'center',
      border: `2px solid ${checked ? accentUI : controlBorder}`, bgcolor: checked ? accentUI : 'transparent',
      color: accentInk, transition: 'background-color .15s, border-color .15s',
    }}>
      {checked && <Check sx={{ fontSize: 15 }} />}
    </Box>
  );

  const Badge = () => (
    <Box component="span" data-testid="selo-recomendado" sx={{ display: 'inline-block', px: 0.9, py: 0.2, borderRadius: 1, fontSize: 12, fontWeight: 700, lineHeight: 1.3, bgcolor: T ? '#14532d' : '#dcfce7', color: T ? '#bbf7d0' : '#166534', whiteSpace: 'nowrap' }}>
      Recomendado
    </Box>
  );

  const radius = (i: number) => {
    const first = i === 0, last = i === options.length - 1; // 12 px = raio do grupo (14) menos a borda (2)
    return `${first ? 12 : 0}px ${first ? 12 : 0}px ${last ? 12 : 0}px ${last ? 12 : 0}px`;
  };

  const renderRow = (o: any, i: number) => {
    const sel = value === o.id;
    return (
      <Box component="label" key={o.id} sx={{
        position: 'relative', display: 'block', cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.65 : 1, transition: 'opacity .15s',
        borderTop: i > 0 ? `1px solid ${neutralBorder}` : 'none',
        '& input:focus-visible + .po-surface': { outline: `3px solid ${ring}`, outlineOffset: -3 },
      }}>
        <input type="radio" name="pay-method" value={o.id} checked={sel} disabled={!!disabled} onChange={() => onChange(o.id)}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', margin: 0, opacity: 0, cursor: disabled ? 'default' : 'pointer' }} />
        <Box className="po-surface" sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, px: 1.75, py: 1.1, minHeight: 60, borderRadius: radius(i),
          bgcolor: sel ? `${accentColor}12` : 'transparent', boxShadow: sel ? `inset 0 0 0 2px ${accentUI}` : 'none', transition: 'all .15s', pointerEvents: 'none',
        }}>
          <Box sx={{ width: 28, display: 'grid', placeItems: 'center', flexShrink: 0 }}>{o.icon}</Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1, rowGap: 0.4 }}>
              <Typography component="span" sx={{ fontSize: 15, fontWeight: 700, lineHeight: 1.3, color: textP }}>{o.title}</Typography>
              {showBadge(o) && <Badge />}
            </Box>
            <Typography component="span" sx={{ display: 'block', fontSize: 13, lineHeight: 1.4, color: textS, mt: 0.2 }}>{o.desc}</Typography>
          </Box>
          <Control checked={sel} />
        </Box>
      </Box>
    );
  };

  return (
    // O Dialog do agendamento tem width:auto no computador (largura = conteúdo). Para a escolha de pagamento não alterar a
    // estrutura do modal da empresa 7: mínimo de 421 px (a largura útil que o seletor atual dá hoje ao modal dela: 485 px no total)
    // e contain:inline-size para que o texto das opções nunca alargue nem encolha o modal.
    <Box data-testid="pay-selector" data-variant="B" sx={{ width: '100%', minWidth: { sm: 421 }, contain: 'inline-size' }}>
      <Typography component="h3" id="pay-method-label" sx={{ fontSize: 15, fontWeight: 700, color: textP, mb: 0.4 }}>
        Como você quer pagar{isPartial ? ' o sinal' : ''}?
      </Typography>
      <Typography aria-live="polite" sx={{ fontSize: 13, lineHeight: 1.45, color: textS, mb: 1.4 }}>{summary}</Typography>
      <Box role="radiogroup" aria-labelledby="pay-method-label" data-testid="pay-options" sx={{ border: `2px solid ${neutralBorder}`, borderRadius: '14px', overflow: 'hidden', bgcolor: surface }}>
        {options.map((o, i) => renderRow(o, i))}
      </Box>
    </Box>
  );
}
